import React, { useEffect, useState, useRef, lazy, memo, createElement, Component } from 'react';
import { CheckCircle2, XCircle, Star, Zap, ShieldCheck, Users, Check, Beef, Calculator, TrendingUp, Utensils, Smartphone, MessageCircle, Award, Globe, CreditCard } from 'lucide-react';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// Animations are now defined in index.css to avoid render-blocking JS
// --- Sub-components ---
const CountdownTimer = memo(() => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 27,
    seconds: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex gap-2 font-mono text-xl md:text-2xl font-bold text-white" data-id="element-1">
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-2">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <span className="text-white" data-id="element-3">:</span>
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-4">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <span className="text-white" data-id="element-5">:</span>
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-6">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>;
});
const ExitIntentPopup = memo(({
  onClose
}: {
  onClose: () => void;
}) => {
  return <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-7">
      <div className="bg-white rounded-3xl p-8 md:p-12 max-w-xl w-full shadow-2xl relative border-4 border-[#800000]" data-id="element-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-9">
          <XCircle size={32} data-id="element-10" />
        </button>

        <div className="text-center" data-id="element-11">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-[#800000] mx-auto mb-6" data-id="element-12">
            <Users size={40} data-id="element-13" />
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-14">
            ¡No te quedes fuera de la red de carnicerías rentables de LATAM!
          </h2>

          <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-15">
            Sumate a más de{' '}
            <span className="font-bold text-[#800000]" data-id="element-16">
              1.200 dueños de negocios
            </span>{' '}
            en 15 países que ya están optimizando sus márgenes.
          </p>

          <div className="flex flex-col gap-4" data-id="element-17">
            <CTAButton success href="#pricing" onClick={onClose} className="w-full py-4 text-xl" data-id="element-18">
              SÍ, QUIERO EL SISTEMA COMPLETO.
            </CTAButton>

            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 font-medium text-sm underline underline-offset-4" data-id="element-19">
              No, prefiero seguir perdiendo dinero con mis proveedores
            </button>
          </div>
        </div>
      </div>
    </div>;
});
const FloatingNotification = memo(() => {
  const [visible, setVisible] = useState(false);
  const [animClass, setAnimClass] = useState('notif-enter');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasScrolledRef = useRef(false);
  const notifications = [{
    n: 'Luis',
    l: 'CDMX, México',
    t: 'hace 2 min',
    m: 'acaba de adquirir el Sistema 2026'
  }, {
    n: 'Andrés',
    l: 'Bogotá, Colombia',
    t: 'hace 5 min',
    m: 'comenzó su transformación hoy'
  }, {
    n: 'Carlos',
    l: 'Santiago, Chile',
    t: 'hace 8 min',
    m: 'se unió a la comunidad'
  }, {
    n: 'Javier',
    l: 'Madrid, España',
    t: 'hace 12 min',
    m: 'accedió al Método de Despiece'
  }, {
    n: 'Miguel',
    l: 'Lima, Perú',
    t: 'hace 15 min',
    m: 'descargó sus Bonos de Regalo'
  }, {
    n: 'Roberto',
    l: 'Guayaquil, Ecuador',
    t: 'hace 20 min',
    m: 'acaba de comprar'
  }, {
    n: 'Diego',
    l: 'Montevideo, Uruguay',
    t: 'hace 25 min',
    m: 'ya tiene su acceso'
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) hasScrolledRef.current = true;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const showNext = () => {
      if (!hasScrolledRef.current) return;
      setAnimClass('notif-enter');
      setVisible(true);
      setTimeout(() => {
        setAnimClass('notif-exit');
        setTimeout(() => setVisible(false), 300);
      }, 4000);
    };
    const initialDelay = setTimeout(() => {
      if (hasScrolledRef.current) showNext();
    }, 3000);
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % notifications.length);
      showNext();
    }, 12000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);
  const current = notifications[currentIndex];
  if (!visible) return null;
  return <div className={`fixed top-6 left-6 z-50 bg-white shadow-2xl rounded-lg p-4 border border-slate-100 flex items-center gap-4 max-w-sm ${animClass}`} data-id="element-20">
      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-[#800000]" data-id="element-21">
        <Check size={20} data-id="element-22" />
      </div>
      <div data-id="element-23">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-24">
          Compra verificada
        </p>
        <p className="text-sm text-slate-800" data-id="element-25">
          <span className="font-bold" data-id="element-26">{current.n}</span> de {current.l}
          <br data-id="element-27" />
          <span className="font-medium" data-id="element-28">
            {(current as any).m || 'compró sistema completo'}
          </span>{' '}
          🥩
        </p>
        <p className="text-xs text-slate-400 mt-1" data-id="element-29">{current.t}</p>
      </div>
      <button onClick={() => setVisible(false)} className="text-slate-300 hover:text-slate-500" aria-label="Cerrar notificación" data-id="element-30">
        <XCircle size={16} data-id="element-31" />
      </button>
    </div>;
});
const WhatsAppFloating = memo(() => {
  const message = encodeURIComponent('Hola! Vengo de la página Carnicería rentable 2026 y necesito ayuda con mi pago. ¿Me podrías asesorar?');
  return <div className="fixed bottom-6 right-6 z-[100] flex items-center gap-2" data-id="element-32">
      <div className="bg-white text-slate-800 px-3 py-2 rounded-full shadow-xl text-xs md:text-sm font-semibold border border-slate-100 hidden sm:block animate-pulse-slow" data-id="element-33">
        ¿Dudas con tu pago? Escribime.
      </div>
    </div>;
});
const SectionTitle = memo(({
  children,
  subtitle,
  light = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
}) => <div className="text-center mb-6 md:mb-10" data-id="element-34">
      <h2 className={`text-2xl md:text-5xl font-bold mb-2 md:mb-4 ${light ? 'text-white' : 'text-[#121212]'}`} data-id="element-35">
        {children}
      </h2>
      {subtitle && <p className={`text-sm md:text-lg ${light ? 'text-slate-200' : 'text-slate-600'} max-w-2xl mx-auto font-sans px-4`} data-id="element-36">
          {subtitle}
        </p>}
    </div>);
const CTAButton = memo(({
  children,
  secondary = false,
  whatsapp = false,
  success = false,
  className = '',
  href = '',
  onClick
}: {
  children: React.ReactNode;
  secondary?: boolean;
  whatsapp?: boolean;
  success?: boolean;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const buttonEl = <div className="flex flex-col items-center w-full" data-id="element-37">
        <button className={`
          cta-pulse w-full md:w-auto px-10 py-5 rounded-full font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-xl
          ${whatsapp ? 'bg-[#25D366] text-white hover:bg-[#128C7E]' : success ? 'bg-[#16A34A] text-white hover:bg-[#15803D]' : secondary ? 'bg-white text-[#800000] border-2 border-slate-200 hover:bg-slate-50' : 'bg-[#800000] text-white hover:bg-[#600000]'}
          ${className}
        `} onClick={onClick} data-id="element-38">
          {children}
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/2ntHQHLqDKSYj8Nc8XZCqk/garantia-7-dias-250.webp" alt="Garantía 7 días" className="w-20 md:w-28 h-auto mt-4" loading="lazy" decoding="async" data-id="element-39" />
      </div>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-40">
          {buttonEl}
        </a>;
  }
  return buttonEl;
});
// Static data arrays (defined outside component to avoid re-creation)
const statsData = [{
  label: 'Carniceros',
  value: '+1.200'
}, {
  label: 'Módulos',
  value: '6'
}, {
  label: 'Páginas',
  value: '350+'
}, {
  label: 'Calificación',
  value: '4.9/5',
  stars: true
}];
const testimonialsData = [{
  n: 'Jorge L.',
  l: 'Ciudad de México',
  q: 'Con la calculadora del Bono 1 supe exactamente cómo ajustar mis precios para no perder rentabilidad con cada subida de mis proveedores.',
  i: 'JL'
}, {
  n: 'Ricardo M.',
  l: 'Tablajero Profesional',
  q: 'Empecé a hacer mis propios embutidos y elaborados premium y hoy son mi producto más vendido.',
  i: 'RM'
}, {
  n: 'Carlos R.',
  l: 'Bogotá, Colombia',
  q: 'Por fin un sistema que habla nuestro idioma y entiende la realidad de cualquier carnicería de Latinoamérica.',
  i: 'CR'
}];
const problemItems = ['Sin saber calcular el Costo de Reposición real frente a tus proveedores.', 'Con mermas invisibles que se comen tu rentabilidad.', 'Perdiendo clientes por no ofrecer cortes de autor.', 'Con miedo a las inspecciones y controles sanitarios.'];
const modulesData = [{
  t: 'MÓDULO 1: ANATOMÍA BOVINA Y DESPIECE PROFESIONAL',
  d: 'Aprovechamiento total de la res bajo filosofía Zero Waste. Incluye Diccionario Maestro de Equivalencia Internacional de Cortes (México, Colombia, Chile, España, Argentina y más).',
  iconName: 'Beef'
}, {
  t: 'MÓDULO 2: INGENIERÍA DE LA MERMA',
  d: 'Recuperación de grasas y huesos. Cómo convertir el desperdicio en capital y rentabilidad real, incluso en contextos inflacionarios.',
  iconName: 'TrendingUp'
}, {
  t: 'MÓDULO 3: PSICOLOGÍA DEL MOSTRADOR',
  d: 'Cómo subir el ticket promedio. El arte de vender lo que a vos te conviene, no solo lo que el cliente pide.',
  iconName: 'Users'
}, {
  t: 'MÓDULO 4: LA RES ENTERA',
  d: 'Rentabilidad en cortes de segunda. Secretos para que cada corte económico rinda como uno premium.',
  iconName: 'Utensils'
}];
const bonusesData = [{
  t: 'BONO 1: INGENIERÍA DE COSTOS Y RENDIMIENTO CÁRNICO 2026',
  d: 'Calculadora anti-inflación. Ingresás el precio del kilo vivo y obtenés el costo y rentabilidad exacta de cada corte.',
  iconName: 'Calculator'
}, {
  t: 'BONO 2: CORTES BOUTIQUE Y ALTA GASTRONOMÍA CÁRNICA',
  d: 'Tomahawk y cortes de selección. Cómo cobrar el doble por la misma materia prima.',
  iconName: 'Star'
}, {
  t: 'BONO 3: ELABORADOS ARTESANALES Y EMBUTIDOS DE AUTOR',
  d: 'Embutidos y elaborados de autor. El margen real está en lo que vos mismo fabricás.',
  iconName: 'Utensils'
}, {
  t: 'BONO 4: PROTOCOLOS DE INOCUIDAD Y CONTROL SANITARIO',
  d: 'Checklist de inocuidad. Que cualquier inspección sanitaria entre y salga sin observaciones.',
  iconName: 'ShieldCheck'
}, {
  t: 'BONO 5: VENTA DIGITAL CÁRNICA',
  d: 'Venta digital por estados y grupos de WhatsApp. Tu carnicería abierta las 24 horas.',
  iconName: 'Smartphone'
}];
const pricingFeatures = ['Módulos de Despiece y Desposte Profesional', 'Ingeniería de Costos y Rendimiento Cárnico', 'Cortes Boutique y Alta Gastronomía', 'Protocolos de Inocuidad y Control Sanitario', 'Estrategia de Venta Digital', 'Acceso inmediato para siempre'];
const comparisonData = [{
  s: 'Suben los proveedores',
  no: 'Perdés margen o clientes',
  yes: 'Blindás tu rentabilidad con la calculadora.'
}, {
  s: 'Llega una inspección sanitaria',
  no: 'Miedo y riesgo de multas',
  yes: 'Protocolo Bono 4: Todo en regla.'
}, {
  s: 'Sobran recortes',
  no: 'Se tiran o se regalan',
  yes: 'Bono 3: Se convierten en embutidos y elaborados de autor.'
}];
const iconMap: Record<string, ComponentType<any>> = {
  Beef,
  TrendingUp,
  Users,
  Utensils,
  Calculator,
  Star,
  ShieldCheck,
  Smartphone
};
// --- Main Page ---
export function LandingPage() {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const hasShownExitPopup = useRef(false);
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShownExitPopup.current) {
        setShowExitPopup(true);
        hasShownExitPopup.current = true;
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);
  useEffect(() => {
    // PERFORMANCE: Load Google Fonts in a non-render-blocking way.
    // The CSS file is fetched with media="print" (so the browser does not
    // block render on it) and then we flip it to media="all" once loaded.
    // Combined with the size-adjust fallback @font-faces in index.css, this
    // eliminates the FOUT-caused layout shift while keeping LCP fast.
    const fontsHref = 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Playfair+Display:wght@700;800&display=swap';
    if (!document.querySelector(`link[href="${fontsHref}"]`)) {
      const fontsLink = document.createElement('link');
      fontsLink.rel = 'stylesheet';
      fontsLink.href = fontsHref;
      fontsLink.media = 'print';
      fontsLink.onload = () => {
        fontsLink.media = 'all';
      };
      document.head.appendChild(fontsLink);
    }
    // Preconnect to image CDN to speed up LCP image load
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://cdn.magicpatterns.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
    // Defer Meta Pixel loading to avoid blocking LCP / TBT on mobile
    const loadPixel = () => {
      if (window.fbq) return;
      const n: any = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = !0;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
      window.fbq('init', '929826529816985');
      window.fbq('track', 'PageView');
    };
    // Use requestIdleCallback when available, fallback to setTimeout
    const idle: any = (window as any).requestIdleCallback;
    let timeoutId: number | undefined;
    let idleId: number | undefined;
    if (typeof idle === 'function') {
      idleId = idle(loadPixel, {
        timeout: 3500
      });
    } else {
      timeoutId = window.setTimeout(loadPixel, 2500);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId && (window as any).cancelIdleCallback) {
        ;
        (window as any).cancelIdleCallback(idleId);
      }
    };
  }, []);
  return <main className="min-h-screen font-sans bg-[#F9F6F2]" data-id="element-41">
      <FloatingNotification data-id="element-42" />
      <WhatsAppFloating data-id="element-43" />
      {showExitPopup && <ExitIntentPopup onClose={() => setShowExitPopup(false)} data-id="element-44" />}

      {/* Top Banner */}

      {/* Hero Section */}
      <header className="relative bg-[#121212] md:min-h-[90svh] md:h-auto flex flex-col text-white overflow-hidden" data-id="element-45">
        <div className="absolute inset-0 opacity-20" data-id="element-46">
          <div className="absolute inset-0 bg-gradient-to-b from-[#800000]/20 to-transparent" data-id="element-47"></div>
        </div>

        <div className="container mx-auto px-4 flex-grow flex flex-col justify-center py-8 md:py-16 relative z-10" data-id="element-48">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center h-full justify-center" data-id="element-49">
            <div className="flex flex-col items-center" data-id="element-50">
              <div className="fade-in-up inline-flex items-center gap-2 bg-[#800000]/20 border border-[#800000]/40 px-3 py-0.5 rounded-full text-[#F9F6F2] text-[9px] md:text-sm font-bold mb-2 md:mb-8 backdrop-blur-sm" data-id="element-51">
                🥩 SISTEMA CARNICERÍAS RENTABLES 2026™
              </div>

              <h1 className="text-[22px] md:text-6xl font-bold mb-3 md:mb-8 text-white leading-tight" data-id="element-52">
                ¿TUS PROVEEDORES SE QUEDAN CON TU GANANCIA?{' '}
                <span className="text-[#e0a96d] block mt-2 md:mt-4 text-[18px] md:text-4xl font-bold normal-case" data-id="element-53">
                  Deja de trabajar para la cadena de suministro y empieza a
                  maximizar cada gramo de carne.
                </span>
              </h1>

              <p className="text-sm md:text-2xl text-white/80 mb-4 md:mb-8 max-w-3xl mx-auto leading-relaxed px-4 italic" data-id="element-54">
                El método probado para Dueños de Carnicerías, Emprendedores
                Cárnicos y Tablajeros Profesionales que quieren defender su
                rentabilidad y blindar su capital contra la inflación.
              </p>
            </div>

            <div className="relative max-w-[240px] md:max-w-3xl mx-auto mb-4 md:mb-12" data-id="element-55">
              <div className="relative z-10 bg-gradient-to-br from-zinc-800 to-zinc-900 p-2 md:p-4 rounded-xl md:rounded-2xl shadow-2xl border border-zinc-700" data-id="element-56">
                <img src="https://cdn.magicpatterns.com/uploads/gukH4ts1JW6siAhQCCrm1g/a_well_lit_indoor_butcher_shop_scene_overall_view_1_web.webp" alt="Sistema Carnicerías Rentables Mockup" className="w-full h-auto rounded-lg md:rounded-xl shadow-lg" width={1200} height={800} fetchPriority="high" decoding="async" data-id="element-57" />
                <div className="absolute -bottom-2 -right-2 bg-[#800000] text-white px-2 py-1 md:p-4 rounded-lg shadow-xl font-bold text-[10px] md:text-base" data-id="element-58">
                  EDICIÓN 2026
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center w-full" data-id="element-59">
              <CTAButton href="#pricing" className="uppercase tracking-wide px-6 py-3 md:px-12 md:py-6 text-sm md:text-xl mb-3 md:mb-6" data-id="element-60">
                → QUIERO TRANSFORMAR MI NEGOCIO
              </CTAButton>

              <div className="flex justify-center gap-3 md:gap-6 text-white/60 font-medium text-[8px] md:text-sm mb-4 md:mb-6" data-id="element-61">
                <div className="flex items-center gap-1 bg-white/5 px-2 py-0.5 rounded-full border border-white/10" data-id="element-62">
                  +350 Páginas prácticas
                </div>
                <div className="flex items-center gap-1 bg-white/5 px-2 py-0.5 rounded-full border border-white/10" data-id="element-63">
                  <Zap size={10} className="md:w-3.5 md:h-3.5" data-id="element-64" /> Acceso
                  Instantáneo
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-2 md:p-6 shadow-2xl inline-block self-center hidden md:block" data-id="element-65">
              <p className="text-white/60 font-bold uppercase tracking-widest text-[10px] md:text-xs mb-2 md:mb-3" data-id="element-66">
                ⏰ EL PRECIO PROMOCIONAL CIERRA EN:
              </p>
              <CountdownTimer data-id="element-67" />
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-6 md:py-12 bg-[#800000] text-white" data-id="element-68">
        <div className="container mx-auto px-4" data-id="element-69">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto text-center" data-id="element-70">
            {statsData.map((stat, i) => <div key={i} data-id="element-71">
                <p className="text-3xl md:text-4xl font-bold mb-1" data-id="element-72">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-yellow-500 mb-1" data-id="element-73">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-74" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-widest font-bold opacity-80" data-id="element-75">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 md:py-24 bg-white" data-id="element-76">
        <div className="container mx-auto px-4" data-id="element-77">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-78">
            {testimonialsData.map((test, i) => <div key={i} className="bg-[#F9F6F2] p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-79">
                <div className="flex gap-1 mb-4 text-[#800000]" data-id="element-80">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-81" />)}
                </div>
                <p className="text-[#121212] italic mb-6 leading-relaxed" data-id="element-82">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-83">
                  <div className="w-10 h-10 bg-[#800000] rounded-full flex items-center justify-center font-bold text-white" data-id="element-84">
                    {test.i}
                  </div>
                  <div data-id="element-85">
                    <p className="font-bold text-[#121212]" data-id="element-86">{test.n}</p>
                    <p className="text-xs text-slate-500" data-id="element-87">{test.l}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10 md:py-24 bg-[#121212] text-white" data-id="element-88">
        <div className="container mx-auto px-4" data-id="element-89">
          <SectionTitle light subtitle="Llega la mercadería de tus distribuidores. Los precios subieron. Los clientes se quejan. No es falta de oficio, es falta de un sistema de gestión real." data-id="element-90">
            ¿Cuántas reses vas a despiezar sintiendo que el dinero se te escapa
            de las manos?
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-3 md:gap-4 max-w-4xl mx-auto mt-8 md:mt-12" data-id="element-91">
            {problemItems.map((item, i) => <div key={i} className="bg-white/5 border border-white/10 p-4 md:p-6 rounded-xl flex gap-3 md:gap-4 items-start" data-id="element-92">
                <XCircle className="text-[#800000] shrink-0 mt-1" size={20} data-id="element-93" />
                <p className="text-white/90 font-medium text-base md:text-lg" data-id="element-94">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="mt-10 md:mt-16 max-w-3xl mx-auto bg-[#800000]/10 border-l-8 border-[#800000] p-6 md:p-8 rounded-r-2xl shadow-sm text-center" data-id="element-95">
            <p className="text-2xl md:text-3xl font-bold text-white italic" data-id="element-96">
              ¿Y si esta fuera la última vez que enfrentás una suba de tus
              proveedores sin saber qué hacer?
            </p>
          </div>
        </div>
      </section>

      {/* Product Hero */}
      <section className="py-10 md:py-24 bg-white" data-id="element-97">
        <div className="container mx-auto px-4 text-center" data-id="element-98">
          <div className="inline-block bg-[#800000] text-white px-5 py-1.5 rounded-full font-bold text-xs uppercase tracking-widest mb-6 md:mb-8" data-id="element-99">
            SOLUCIÓN INTEGRAL
          </div>
          <h2 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6 text-[#121212]" data-id="element-100">
            🥩 SISTEMA CARNICERÍAS RENTABLES 2026™
          </h2>
          <p className="text-base md:text-2xl text-slate-600 mb-8 md:mb-12 max-w-3xl mx-auto" data-id="element-101">
            Ebook Central + 5 Bonos Estratégicos. 100% adaptado a la realidad de
            toda Latinoamérica y España. No más improvisación.
          </p>

          <div className="max-w-4xl mx-auto mb-8 md:mb-16 relative" data-id="element-102">
            <img src="https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&q=80&w=800" alt="Pack Completo" className="w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl border border-slate-200" loading="lazy" decoding="async" width={800} height={533} data-id="element-103" />
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-10 md:py-24 bg-[#F9F6F2]" data-id="element-104">
        <div className="container mx-auto px-4" data-id="element-105">
          <SectionTitle subtitle="La estructura maestra para transformar tu negocio." data-id="element-106">
            📚 MÓDULOS DEL EBOOK CENTRAL
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-107">
            {modulesData.map((m, i) => {
            const IconComp = iconMap[m.iconName];
            return <div key={i} className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-[#800000]" data-id="element-108">
                  <div className="text-[#800000] mb-4" data-id="element-109">
                    {IconComp && <IconComp size={24} data-id="element-110" />}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#121212]" data-id="element-111">
                    {m.t}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-112">{m.d}</p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-10 md:py-24 bg-white" data-id="element-113">
        <div className="container mx-auto px-4" data-id="element-114">
          <SectionTitle subtitle="Valor total bonos: $55.000 → GRATIS con tu pack" data-id="element-115">
            🎁 5 BONOS ESTRATÉGICOS
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto" data-id="element-116">
            {bonusesData.map((b, i) => {
            const IconComp = iconMap[b.iconName];
            return <div key={i} className="bg-[#F9F6F2] p-6 rounded-2xl border border-slate-100 relative overflow-hidden" data-id="element-117">
                  <div className="absolute top-0 right-0 bg-[#800000] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase" data-id="element-118">
                    Gratis
                  </div>
                  <div className="text-[#800000] mb-4" data-id="element-119">
                    {IconComp && <IconComp size={24} data-id="element-120" />}
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-[#121212]" data-id="element-121">
                    {b.t}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-122">
                    {b.d}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-6 md:py-16 bg-[#121212] text-white" id="pricing" data-id="element-123">
        <div className="container mx-auto px-4" data-id="element-124">
          <div className="text-center mb-4 md:mb-8" data-id="element-125">
            <h2 className="text-2xl md:text-5xl font-bold mb-2 text-white" data-id="element-126">
              LLEVATE TU PACK COMPLETO ⭐
            </h2>
            <p className="text-sm md:text-lg text-slate-200 max-w-2xl mx-auto font-sans px-4" data-id="element-127">
              Acceso inmediato a todo el material en formato PDF.
            </p>
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-128">
            <div className="bg-[#1a1a1a] rounded-2xl md:rounded-3xl p-5 md:p-10 text-white shadow-2xl flex flex-col border-4 border-[#800000] relative" data-id="element-129">
              <div className="text-center mb-3 md:mb-6" data-id="element-130">
                <h3 className="text-lg md:text-2xl font-bold mb-1" data-id="element-131">
                  SISTEMA CARNICERÍAS RENTABLES
                </h3>
                <p className="text-white/60 text-[10px] md:text-xs" data-id="element-132">
                  Ebook Central + 5 Bonos
                </p>
              </div>

              <div className="text-center mb-3 md:mb-6 flex flex-col items-center" data-id="element-133">
                <p className="text-zinc-500 line-through text-xs md:text-lg mb-2" data-id="element-134">
                  $49 USD
                </p>
                <div className="inline-block bg-white px-6 py-3 rounded-2xl shadow-2xl transform -rotate-1 border-2 border-[#800000]/10" data-id="element-135">
                  <p className="text-4xl md:text-7xl font-bold text-[#800000] leading-none" data-id="element-136">
                    $14.99 USD
                  </p>
                  <p className="text-[10px] md:text-sm font-bold text-[#800000]/60 uppercase tracking-widest mt-1" data-id="element-137">
                    Precio de Lanzamiento
                  </p>
                </div>
                <p className="text-[#e0a96d] font-bold text-[11px] md:text-sm mt-3 max-w-md" data-id="element-138">
                  🔥 Oferta válida para todo Habla Hispana - Conversión
                  automática a moneda local disponible.
                </p>
              </div>

              <div className="flex flex-col items-center mb-4 md:mb-8" data-id="element-139">
                <p className="text-white/60 font-bold uppercase tracking-widest text-[9px] md:text-[10px] mb-2" data-id="element-140">
                  ⏰ LA OFERTA TERMINA EN:
                </p>
                <CountdownTimer data-id="element-141" />
              </div>

              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 md:gap-y-3 mb-5 md:mb-8" data-id="element-142">
                {pricingFeatures.map((item, i) => <li key={i} className="flex items-center gap-2 text-[11px] md:text-sm font-medium" data-id="element-143">
                    <CheckCircle2 size={14} className="text-[#800000] shrink-0 md:w-4 md:h-4" data-id="element-144" />
                    <span data-id="element-145">{item}</span>
                  </li>)}
              </ul>

              {/* Payment Info Block */}
              <div className="flex flex-col items-center mb-6 space-y-4" data-id="element-146">
                <div className="bg-[#F0FDF4] p-3 rounded-lg border border-[#DCFCE7] w-full" data-id="element-147">
                  <p className="text-[13px] text-[#14532D] text-center leading-tight" data-id="element-148">
                    📦{' '}
                    <span className="font-bold" data-id="element-149">
                      ENTREGA DIGITAL INSTANTÁNEA:
                    </span>{' '}
                    Sin costos de envío. Recibís el acceso en tu email al
                    instante.
                  </p>
                </div>
              </div>

              {/* International Trust Block */}
              <div className="mb-6 md:mb-8 bg-white/5 border border-white/15 rounded-2xl p-5 md:p-7 shadow-inner" data-id="element-150">
                <div className="text-center mb-4 md:mb-5" data-id="element-151">
                  <h4 className="text-base md:text-xl font-bold text-white" data-id="element-152">
                    🌎 ACCESO GLOBAL E INSTANTÁNEO
                  </h4>
                  <p className="text-white/70 text-[11px] md:text-sm leading-relaxed mt-2 max-w-md mx-auto" data-id="element-153">
                    Este sistema ha sido diseñado para funcionar en cualquier
                    país de habla hispana. No importa dónde te encuentres,
                    nuestra plataforma procesa tu pedido de forma segura y
                    transparente.
                  </p>
                </div>

                <ul className="space-y-3 md:space-y-4" data-id="element-154">
                  <li className="flex items-start gap-3" data-id="element-155">
                    <div className="w-8 h-8 md:w-9 md:h-9 shrink-0 rounded-full bg-[#16A34A]/15 flex items-center justify-center" data-id="element-156">
                      <CreditCard size={18} className="text-[#16A34A]" data-id="element-157" />
                    </div>
                    <p className="text-[11px] md:text-sm text-white/80 leading-relaxed" data-id="element-158">
                      <span className="font-bold text-white" data-id="element-159">
                        Conversión Automática:
                      </span>{' '}
                      Nuestra pasarela de pagos convertirá automáticamente los
                      $14.99 USD a tu moneda local al tipo de cambio oficial del
                      momento. Podrás pagar con tu tarjeta local (Débito o
                      Crédito) sin complicaciones.
                    </p>
                  </li>
                  
                  <li className="flex items-start gap-3" data-id="element-160">
                    <div className="w-8 h-8 md:w-9 md:h-9 shrink-0 rounded-full bg-[#16A34A]/15 flex items-center justify-center" data-id="element-161">
                      <Zap size={18} className="text-[#16A34A]" data-id="element-162" />
                    </div>
                    <p className="text-[11px] md:text-sm text-white/80 leading-relaxed" data-id="element-163">
                      <span className="font-bold text-white" data-id="element-164">
                        Entrega Inmediata:
                      </span>{' '}
                      Recibirás el acceso a la plataforma en tu correo
                      electrónico segundos después de completar tu compra, sin
                      importar la hora o el país.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col items-center w-full" data-id="element-165">
                <CTAButton success href="https://sistemas-carnicerias-rentables2026.impultienda.ar/checkout" className="w-full py-3.5 md:py-5 text-[13px] md:text-lg leading-tight" data-id="element-166">
                  SÍ, QUIERO EL SISTEMA COMPLETO.
                </CTAButton>
                <div className="mt-4 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full border border-white/10 shadow-sm" data-id="element-167">
                  <p className="text-[10px] md:text-xs text-white/90 font-medium" data-id="element-168">
                    🔒 Oferta válida para todo Habla Hispana - Conversión
                    automática a moneda local disponible.
                  </p>
                </div>
              </div>

              {/* Purchase Support Icons */}
              <div className="grid grid-cols-3 gap-2 mt-8 pt-8 border-t border-white/10 text-center" data-id="element-169">
                <div className="flex flex-col items-center space-y-2" data-id="element-170">
                  <ShieldCheck size={20} className="text-[#16A34A]" data-id="element-171" />
                  <p className="text-[9px] md:text-[11px] text-white/60 leading-tight" data-id="element-172">
                    Compra Protegida por Mercado Pago
                  </p>
                </div>
                <div className="flex flex-col items-center space-y-2" data-id="element-173">
                  <Zap size={20} className="text-[#16A34A]" data-id="element-174" />
                  <p className="text-[9px] md:text-[11px] text-white/60 leading-tight" data-id="element-175">
                    Acceso Inmediato (Formato PDF)
                  </p>
                </div>
                <div className="flex flex-col items-center space-y-2" data-id="element-176">
                  <Award size={20} className="text-[#16A34A]" data-id="element-177" />
                  <p className="text-[9px] md:text-[11px] text-white/60 leading-tight" data-id="element-178">
                    7 Días de Garantía de Satisfacción
                  </p>
                </div>
              </div>

              <p className="mt-3 md:mt-5 text-center text-[#800000] font-bold text-[9px] md:text-sm" data-id="element-179">
                ⚠️ Después del contador, el precio de lanzamiento finaliza y
                sube a $49 USD.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-10 md:py-24 bg-white" data-id="element-180">
        <div className="container mx-auto px-4" data-id="element-181">
          <SectionTitle subtitle="La diferencia entre un negocio que sobrevive y uno que prospera." data-id="element-182">
            ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
          </SectionTitle>

          {/* Mobile View */}
          <div className="block md:hidden space-y-4 max-w-sm mx-auto" data-id="element-183">
            {comparisonData.map((row, i) => <div key={i} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-184">
                <div className="bg-[#121212] px-4 py-2 text-xs font-bold text-white uppercase tracking-wider" data-id="element-185">
                  {row.s}
                </div>
                <div className="grid grid-cols-2 text-[11px] leading-tight" data-id="element-186">
                  <div className="bg-red-50 p-4 text-red-700 italic border-r border-white" data-id="element-187">
                    <span className="block font-bold text-[9px] uppercase mb-1 opacity-60" data-id="element-188">
                      Sin el Sistema
                    </span>
                    {row.no}
                  </div>
                  <div className="bg-green-50 p-4 text-green-800 font-bold" data-id="element-189">
                    <span className="block font-bold text-[9px] uppercase mb-1 opacity-60" data-id="element-190">
                      Con el Sistema
                    </span>
                    {row.yes}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block max-w-4xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xl" data-id="element-191">
            <table className="w-full text-left border-collapse" data-id="element-192">
              <thead data-id="element-193">
                <tr className="bg-[#121212] text-white" data-id="element-194">
                  <th className="p-6 font-bold" data-id="element-195">SITUACIÓN</th>
                  <th className="p-6 font-bold" data-id="element-196">❌ SIN EL SISTEMA</th>
                  <th className="p-6 font-bold" data-id="element-197">✅ CON EL SISTEMA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-198">
                {comparisonData.map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-199">
                    <td className="p-6 text-sm font-bold text-[#121212]" data-id="element-200">
                      {row.s}
                    </td>
                    <td className="p-6 text-sm text-red-700 italic" data-id="element-201">
                      {row.no}
                    </td>
                    <td className="p-6 text-sm font-bold text-green-700" data-id="element-202">
                      {row.yes}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-10 md:py-24 bg-[#800000]/5" data-id="element-203">
        <div className="container mx-auto px-4 text-center" data-id="element-204">
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-16 rounded-2xl md:rounded-3xl border-2 border-[#800000] shadow-xl" data-id="element-205">
            <ShieldCheck size={60} className="text-[#800000] mx-auto mb-6 md:mb-8" data-id="element-206" />
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-[#121212]" data-id="element-207">
              GARANTÍA DE SATISFACCIÓN DE 7 DÍAS
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed" data-id="element-208">
              Si el material no mejora tu rentabilidad, te devolvemos el 100%.{' '}
              <br data-id="element-209" />
              <span className="text-xl md:text-2xl font-bold text-[#800000] block mt-2 md:mt-4 uppercase" data-id="element-210">
                Sin preguntas.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-16 bg-[#121212] text-white/40 border-t border-white/5" data-id="element-211">
        <div className="container mx-auto px-4 text-center" data-id="element-212">
          <p className="font-bold text-white text-2xl mb-4" data-id="element-213">
            🥩 SISTEMA CARNICERÍAS RENTABLES™
          </p>
          <p className="text-sm mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-214">
            El método definitivo para el carnicero moderno que busca
            profesionalizar su oficio y proteger su capital.
          </p>
          <p className="text-xs" data-id="element-215">© 2026 — Todos los derechos reservados.</p>
        </div>
      </footer>
    </main>;
}